"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, ChartBar as BarChart3, MapPin, Compass, CircleCheck as CheckCircle2, ArrowRight, Target, Zap, Globe, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
const marketMetrics = [{
  title: "Dundee Monthly Local Searches",
  value: "145,000+",
  change: "+18% YoY",
  description: "High-intent queries for local services, trade, and commercial B2B across Tay Cities region.",
  icon: Search
}, {
  title: "Mobile & Map Pack Ratio",
  value: "68%",
  change: "High Commercial Intent",
  description: "Searches originating on mobile devices seeking immediate local solutions and map directions.",
  icon: MapPin
}, {
  title: "Local Competitor SEO Gap",
  value: "62%",
  change: "Low Technical Optimization",
  description: "Dundee businesses relying on legacy sites lacking structured data and targeted local landing pages.",
  icon: BarChart3
}, {
  title: "AI Search Readiness Opportunity",
  value: "84%",
  change: "Untapped Traffic",
  description: "Local brands missing visibility in Google AI Overviews and conversational local search prompts.",
  icon: Zap
}];
const dundeeKeywords = [{
  term: "Dundee professional services",
  volume: "3,600/mo",
  competition: "Moderate",
  opportunity: "High",
  strategy: "Service-specific landing pages + Schema markup"
}, {
  term: "Commercial B2B supplier Dundee",
  volume: "1,900/mo",
  competition: "Low",
  opportunity: "Very High",
  strategy: "Technical SEO & authoritative lead magnets"
}, {
  term: "Emergency trade services Dundee",
  volume: "5,400/mo",
  competition: "High",
  opportunity: "High",
  strategy: "Google Map Pack & mobile call-to-action optimization"
}, {
  term: "E-commerce & retail Dundee",
  volume: "8,100/mo",
  competition: "Moderate",
  opportunity: "High",
  strategy: "Product category SEO & localized structured data"
}, {
  term: "Tech & creative agency Dundee",
  volume: "2,400/mo",
  competition: "Moderate",
  opportunity: "High",
  strategy: "Thought leadership & AI Search optimization"
}];
const strategicOpportunities = [{
  title: "Google Map Pack & Hyper-Local Dominance",
  description: "I directly optimize your Google Business Profile, geo-targeted content, and localized review signals to ensure you occupy top 3 positions for high-intent Dundee searches.",
  icon: MapPin,
  highlight: "Dominant Local Visibility"
}, {
  title: "Technical Site Architecture & Speed",
  description: "Eliminate underlying crawl blocks, core web vital issues, and indexation bottlenecks that prevent search engines from ranking your Dundee business pages.",
  icon: Zap,
  highlight: "Lightning-Fast Performance"
}, {
  title: "AI Search & GEO (Generative Engine Optimization)",
  description: "Prepare your brand for conversational AI answers across Google Gemini and ChatGPT, capturing Dundee buyers who use modern AI search methods.",
  icon: Globe,
  highlight: "Future-Proofed Organic Reach"
}];
const neighboringHubs = [{
  name: "Sunderland Local SEO",
  path: "/LocalSeoConsultantSunderlandPage",
  region: "North East Coast"
}, {
  name: "Hull SEO Consultancy",
  path: "/LocalSeoConsultantHullPage",
  region: "Maritime & Trade Hub"
}, {
  name: "Preston Local SEO",
  path: "/LocalSeoConsultantPrestonPage",
  region: "Regional Commercial Center"
}, {
  name: "Doncaster SEO Services",
  path: "/LocalSeoConsultantDoncasterPage",
  region: "Logistics & Business Hub"
}, {
  name: "National SEO Strategy",
  path: "/NationalSeoPage",
  region: "UK-Wide Authority"
}, {
  name: "B2B SEO & Lead Gen",
  path: "/B2BSeoLeadGenerationPage",
  region: "High-Value Acquisition"
}];
export default function Dundee() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="dundeemarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        
        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-border">
              <Compass className="size-3.5 mr-1 text-primary" />
              Regional Market Intelligence
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-4">
            Dundee SEO & Search Market Analysis
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I perform deep local search audits and competitive analysis across Dundee and the surrounding Tay Cities area. When you work with me, you work directly with Yassine Essaadi — no agency middle management, just transparent strategies focused on sustainable revenue growth.
          </p>
        </motion.div>

        {/* Market Metrics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketMetrics.map((metric, i) => {
          const Icon = metric.icon;
          return <motion.div key={metric.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border border-border/80 hover:border-primary/50 transition-[border-color,box-shadow] duration-300 shadow-xs hover:shadow-md">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="p-2 rounded-lg bg-primary/10 text-primary">
                        <Icon className="size-5" />
                      </span>
                      <Badge variant="outline" className="text-xs text-primary border-primary/30">
                        {metric.change}
                      </Badge>
                    </div>
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {metric.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl md:text-3xl font-bold font-serif text-foreground mb-2">
                      {metric.value}
                    </div>
                    <CardDescription className="text-xs text-muted-foreground leading-normal">
                      {metric.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Keyword Intelligence Table */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target className="size-5 text-primary" />
                <h3 className="text-xl md:text-2xl font-bold font-serif text-foreground">
                  High-Intent Dundee Search Trends
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Representative keyword clusters and strategic leverage points for businesses serving Dundee.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start md:self-auto">
              <Link to="/LocalSEOPage">
                Explore Local SEO Services
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>

          <Card className="bg-card border border-border shadow-xs overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="font-semibold text-foreground">Search Term Category</TableHead>
                  <TableHead className="font-semibold text-foreground">Est. Search Volume</TableHead>
                  <TableHead className="font-semibold text-foreground">Competition</TableHead>
                  <TableHead className="font-semibold text-foreground">Growth Opportunity</TableHead>
                  <TableHead className="font-semibold text-foreground">Recommended SEO Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dundeeKeywords.map((row, i) => <TableRow key={row.term} data-index={i} className="hover:bg-muted/30">
                    <TableCell className="font-medium text-foreground">
                      {row.term}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {row.volume}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={row.competition === "High" ? "bg-accent/15 text-accent-foreground border-accent/30" : "bg-secondary text-secondary-foreground"}>
                        {row.competition}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        <TrendingUp className="size-3.5" />
                        {row.opportunity}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.strategy}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </Card>
        </motion.div>

        {/* Strategic Pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {strategicOpportunities.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }}>
                <Card className="h-full bg-card border border-border p-6 flex flex-col justify-between hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5">
                      <Icon className="size-6" />
                    </div>
                    <Badge variant="secondary" className="mb-3 text-[13px] font-medium bg-secondary text-secondary-foreground">
                      {item.highlight}
                    </Badge>
                    <h4 className="text-lg font-bold font-serif text-foreground mb-3">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {item.description}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-border/60">
                    <div className="flex items-center gap-2 text-xs font-medium text-primary">
                      <CheckCircle2 className="size-4" />
                      Direct consultation & implementation by Yassine
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Surrounding Locations & Clusters */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-card border border-border rounded-xl p-6 md:p-8 mb-16">
          <div className="max-w-2xl mb-6">
            <h3 className="text-xl font-bold font-serif text-foreground mb-2 flex items-center gap-2">
              <Users className="size-5 text-primary" />
              Cross-Regional SEO Services & Nearby Markets
            </h3>
            <p className="text-sm text-muted-foreground">
              Whether you are scaling within Dundee or expanding into key UK commercial hubs, I build tailored SEO campaigns to capture localized search volume.
            </p>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            {neighboringHubs.map((hub, i) => <div key={hub.name} data-index={i} className="group">
                <Link to={hub.path} className="p-3.5 rounded-lg border border-border bg-background hover:bg-muted/40 transition-colors flex items-center justify-between group-hover:border-primary/50">
                  <div>
                    <div className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      {hub.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {hub.region}
                    </div>
                  </div>
                  <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </Link>
              </div>)}
          </div>
        </motion.div>

        {/* Direct CTA Section */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-12 text-center relative overflow-hidden shadow-lg">
          <div className="max-w-3xl mx-auto relative z-10">
            <Badge className="bg-primary-foreground/15 text-primary-foreground border-transparent mb-4">
              Direct Expert Access
            </Badge>
            <h3 className="text-xl sm:text-2xl font-bold font-serif mb-4 text-primary-foreground">
              Ready to Capture More Dundee Organic Search Traffic?
            </h3>
            <p className="text-primary-foreground/90 text-sm md:text-base leading-relaxed mb-8 max-w-2xl mx-auto">
              You will work directly with me, Yassine Essaadi. No account managers, no automated template reports — just bespoke SEO strategy tailored to your Dundee market goals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold">
                <Link to="/ContactPage">
                  Book a Free Dundee SEO Consultation
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  View Verified Results
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}